/**
 * One review as a list row (Overview, Reviews): status, title, what it was
 * checked against, the counterparty, open gaps by severity, and when.
 *
 * @module components/apps/contractIntelligence/CiReviewRow
 */
import { Link } from "@tanstack/react-router";
import { ClipboardListIcon, LandmarkIcon, ScrollTextIcon } from "lucide-react";

import { cn } from "../../../lib/utils";
import type { CiBasisKind, CiReviewSummary } from "../../../state/contractIntelligence";
import { basisLine, isActive, openGapCount, shortDate } from "./ciModel";
import { ReviewStatusMark, SeverityCounts } from "./ciParts";

export function BasisIcon({ kind, className }: { readonly kind: CiBasisKind; readonly className?: string }) {
  const Icon = kind === "regime" ? LandmarkIcon : kind === "template" ? ClipboardListIcon : ScrollTextIcon;
  return <Icon aria-hidden className={cn("size-3.5 shrink-0", className)} />;
}

export function BasisChip({ basis, className }: { readonly basis: CiReviewSummary["basis"]; readonly className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-0 max-w-56 items-center gap-1 rounded-md border border-border/70 px-1.5 text-[11px] text-muted-foreground",
        className,
      )}
      title={basis.items.map((item) => item.name).join(", ")}
    >
      <BasisIcon kind={basis.kind} className="size-3" />
      <span className="truncate">{basisLine(basis)}</span>
    </span>
  );
}

const counterpartyOf = (review: CiReviewSummary): string | null => {
  const names = [...new Set(review.documents.map((document) => document.counterparty).filter((name): name is string => Boolean(name)))];
  if (names.length === 0) return null;
  return names.length === 1 ? names[0]! : `${names[0]} and ${names.length - 1} more`;
};

export function CiReviewRow({ review, orgSegment }: { readonly review: CiReviewSummary; readonly orgSegment: string }) {
  const open = openGapCount(review);
  const counterparty = counterpartyOf(review);
  return (
    <Link
      to="/$org/apps/contract-intelligence/reviews/$reviewId"
      params={{ org: orgSegment, reviewId: review.id }}
      className="group flex h-11 min-w-0 items-center gap-3 px-4 text-[13px] outline-none transition-colors hover:bg-accent/40 focus-visible:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:px-6"
      data-ci-review-row={review.id}
    >
      <ReviewStatusMark status={review.status} />
      <span className="min-w-0 flex-1 truncate font-medium">{review.title}</span>
      {counterparty ? (
        <span className="hidden min-w-0 max-w-48 truncate text-xs text-muted-foreground md:inline">{counterparty}</span>
      ) : null}
      <BasisChip basis={review.basis} className="hidden sm:inline-flex" />
      <span className="flex w-24 shrink-0 justify-end">
        {review.status === "completed" && open === 0 ? (
          <span className="text-xs text-muted-foreground">No open gaps</span>
        ) : isActive(review.status) ? (
          <span className="text-xs text-muted-foreground">Reviewing…</span>
        ) : review.status === "failed" ? (
          <span className="text-xs text-destructive">Failed</span>
        ) : (
          <SeverityCounts open={review.gaps.open} />
        )}
      </span>
      <time className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground" dateTime={review.createdAt}>
        {shortDate(review.createdAt)}
      </time>
    </Link>
  );
}
